// Team Transcripts List Component - Left column transcript list with search and filtering

import React, { useState, useEffect } from 'react';
import { useTeamTranscripts } from '../../hooks/useTeamTranscripts';
import type { TeamTranscript } from '../../types/teamTranscripts';

interface TeamMember {
  id: string;
  user_id: string;
  user_email: string;
  role: string;
}

interface TeamTranscriptsListProps {
  teamId: string;
  selectedTranscript: TeamTranscript | null;
  onSelectTranscript: (transcript: TeamTranscript) => void;
  refreshTrigger: number;
}

const TeamTranscriptsList: React.FC<TeamTranscriptsListProps> = ({
  teamId,
  selectedTranscript,
  onSelectTranscript,
  refreshTrigger
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const {
    transcripts,
    team,
    loading,
    error,
    hasMore,
    loadMore,
    searchTranscripts,
    filterByMember,
    refresh
  } = useTeamTranscripts(teamId, refreshTrigger);

  // Fetch team members for filter dropdown
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!teamId) return;
      
      setLoadingMembers(true);
      try {
        const response = await fetch(`/api/teams/${teamId}/members`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setTeamMembers(result.members || []);
          }
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchTeamMembers();
  }, [teamId]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Trigger search when debounced query changes
  useEffect(() => {
    searchTranscripts(debouncedSearchQuery);
  }, [debouncedSearchQuery, searchTranscripts]);

  // Handle member filter change
  const handleMemberFilterChange = (memberId: string) => {
    setSelectedMemberId(memberId);
    filterByMember(memberId || null);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return 'Today';
      if (diffDays === 2) return 'Yesterday';
      if (diffDays <= 7) return `${diffDays - 1} days ago`;
      
      return date.toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '';
    }
  };

  if (loading && transcripts.length === 0) {
    return (
      <div className="h-full flex flex-col">
        {/* Search and Filter Controls */}
        <div className="p-4 border-b border-gray-200">
          {/* Search Box */}
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Member Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedMemberId}
              onChange={(e) => handleMemberFilterChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              disabled={loading || loadingMembers}
            >
              <option value="">All Members</option>
              {teamMembers.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user_email} ({member.role})
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Loading State */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team transcripts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        {/* Search and Filter Controls */}
        <div className="p-4 border-b border-gray-200">
          {/* Search Box */}
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Member Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedMemberId}
              onChange={(e) => handleMemberFilterChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              disabled
            >
              <option value="">All Members</option>
              {teamMembers.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user_email} ({member.role})
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Error State */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Transcripts</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={refresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search and Filter Controls */}
      <div className="p-4 border-b border-gray-200">
        {/* Search Box */}
        <div className="relative mb-3">
          <input
            type="text"
            placeholder="Search transcripts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Member Filter Dropdown */}
        <div className="relative">
          <select
            value={selectedMemberId}
            onChange={(e) => handleMemberFilterChange(e.target.value)}
            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            disabled={loadingMembers}
          >
            <option value="">All Members</option>
            {teamMembers.map((member) => (
              <option key={member.user_id} value={member.user_id}>
                {member.user_email} ({member.role})
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Transcripts List */}
      <div className="flex-1 overflow-y-auto">
        {transcripts.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-4xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Transcripts Found</h3>
              <p className="text-gray-600">
                {searchQuery || selectedMemberId ? 'Try adjusting your search or filter' : 'No team transcripts available yet'}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {transcripts.map((transcript) => (
              <div
                key={transcript.id}
                onClick={() => onSelectTranscript(transcript)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedTranscript?.id === transcript.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900 line-clamp-1 flex-1 mr-2">
                    {transcript.title}
                  </h3>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatTime(transcript.created_at)}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {transcript.preview}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      👤 {transcript.owner_email}
                    </span>
                    <span className="flex items-center gap-1">
                      📅 {formatDate(transcript.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs">
                    {transcript.clients_count > 0 && (
                      <span className="flex items-center gap-1 text-purple-600">
                        👥 {transcript.clients_count}
                      </span>
                    )}
                    {transcript.notes_count > 0 && (
                      <span className="flex items-center gap-1 text-green-600">
                        📝 {transcript.notes_count}
                      </span>
                    )}
                    {transcript.action_items_count > 0 && (
                      <span className="flex items-center gap-1 text-orange-600">
                        ✅ {transcript.action_items_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={loadMore}
              disabled={loading}
              className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTranscriptsList;